import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getAcademicYearList } from 'helpers/backend_helper';

interface AcademicYear {
  academicYearId: number;
  academicYearName: string;
}

interface AcademicYearState {
  // State
  academicYears: AcademicYear[];
  selectedAcademicYear: AcademicYear | null;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchAcademicYears: () => Promise<void>;
  setSelectedAcademicYear: (academicYear: AcademicYear | null) => void;
  setAcademicYears: (academicYears: AcademicYear[]) => void;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  academicYears: [],
  selectedAcademicYear: null,
  isLoading: false,
  error: null,
};

export const useAcademicYearStore = create<AcademicYearState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Actions
      fetchAcademicYears: async () => {
        set({ isLoading: true, error: null });
        
        try {
          const response = await getAcademicYearList();
          
          if (response.data && Array.isArray(response.data)) {
            const academicYears = response.data.map((academicYear: any) => ({
              academicYearId: academicYear.academicYearId,
              academicYearName: academicYear.academicYearName,
              startDate: academicYear.startDate,
              endDate: academicYear.endDate,
            }));
            
            set({ academicYears, isLoading: false });
            // Only auto-select if no academic year is currently selected
            if (get().selectedAcademicYear === null) {
              set({ selectedAcademicYear: academicYears[0] });
            }
          } else {
            set({ academicYears: [], isLoading: false });
          }
        } catch (error) {
          console.error('Error fetching academic years:', error);
          set({ 
            error: 'Failed to fetch academic years', 
            isLoading: false 
          });
        }
      },

      setSelectedAcademicYear: (selectedAcademicYear) => set({ selectedAcademicYear }),

      setAcademicYears: (academicYears) => set({ academicYears }),

      clearError: () => set({ error: null }),

      reset: () => set(initialState),
    }),
    {
      name: 'academic-year-storage',
      partialize: (state) => ({
        academicYears: state.academicYears,
        selectedAcademicYear: state.selectedAcademicYear,
      }),
    }
  )
);
